import { useState } from 'react';
import { useApi } from '../lib/api';
import { useList } from '../lib/hooks';
import { useDrawer } from '../lib/drawer';
import { qs, fmtTime, until } from '../lib/util';
import type { CacheRow, NormalizeState } from '../lib/types';
import { Banner, Chip, DataBlock, Empty, ErrorBox, Loading, Pager, SourceChip, Table, Toolbar, cx } from '../ui';

const LIMIT = 50;

const STATE_CLASS: Record<NormalizeState, string> = {
  done: 'border-ok text-ok',
  pending: 'border-warn text-warn',
  failed: 'border-bad text-bad',
  skipped: 'border-line2 text-muted',
};

function Select({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: [string, string][] }) {
  return (
    <select className="input" style={{ width: 'auto' }} value={value} onChange={(e) => onChange(e.target.value)}>
      {options.map(([v, label]) => (
        <option key={v} value={v}>
          {label}
        </option>
      ))}
    </select>
  );
}

export default function Cache() {
  const { request } = useApi();
  const { show } = useDrawer();
  const [source, setSource] = useState('');
  const [kind, setKind] = useState('');
  const [state, setState] = useState('');
  const [op, setOp] = useState('');
  const [keyFilter, setKeyFilter] = useState('');
  const [offset, setOffset] = useState(0);

  const path = `/v1/admin/cache${qs({ source, kind, state, op, limit: LIMIT, offset })}`;
  const { data, loading, error, reload } = useList<CacheRow>(path);

  const reset = (fn: () => void) => {
    fn();
    setOffset(0);
  };

  async function purge(scope: 'expired' | 'all', src?: string) {
    if (scope === 'all' && !window.confirm('Delete ALL cached responses? This cannot be undone.')) return;
    await request('POST', `/v1/admin/cache/purge${qs({ scope, source: src })}`);
    reload();
  }
  async function del(id: number) {
    await request('DELETE', `/v1/admin/cache/${id}`);
    reload();
  }
  async function detail(id: number) {
    const r = await request('GET', `/v1/admin/cache/${id}`);
    show({
      title: `Cache entry #${id}`,
      body: (
        <pre className="mono overflow-auto rounded border border-line bg-panel2 p-3 text-[12px]">
          {JSON.stringify(r.data, null, 2)}
        </pre>
      ),
    });
  }

  const items = (data?.items ?? []).filter((r) => !keyFilter || r.queryKey.toLowerCase().includes(keyFilter.toLowerCase()));

  return (
    <>
      <Banner icon="▤">
        One row per upstream <b>operation</b>, not per search — a Kromeda search is a free-search plus one cross call
        per seed, and each is cached and reused on its own. Proxy and vehicle calls land here too.
      </Banner>
      <Toolbar>
        <Select value={source} onChange={(v) => reset(() => setSource(v))} options={[['', 'All sources'], ['tecdoc', 'TecDoc'], ['kromeda', 'Kromeda']]} />
        <Select value={kind} onChange={(v) => reset(() => setKind(v))} options={[['', 'Any kind'], ['oe', 'oe'], ['article', 'article'], ['vehicle', 'vehicle'], ['passthrough', 'passthrough'], ['aux', 'aux']]} />
        <Select value={state} onChange={(v) => reset(() => setState(v))} options={[['', 'Any state'], ['pending', 'pending'], ['done', 'done'], ['failed', 'failed'], ['skipped', 'skipped']]} />
        <input className="input mono" style={{ width: 150 }} placeholder="Operation…" value={op} onChange={(e) => reset(() => setOp(e.target.value))} />
        <input className="input mono" style={{ flex: 1, minWidth: 160 }} placeholder="Filter keys (this page)…" value={keyFilter} onChange={(e) => setKeyFilter(e.target.value)} />
        <button className="btn btn-sm" onClick={() => purge('expired')}>
          Purge expired
        </button>
        <button className="btn btn-sm btn-danger" onClick={() => purge('all')}>
          Flush all
        </button>
      </Toolbar>
      {error && <ErrorBox>{error}</ErrorBox>}
      <DataBlock
        title="Layer A — response cache"
        sub={data ? `${data.total.toLocaleString()} entries` : undefined}
        raw={data}
        right={data ? <Pager total={data.total} limit={LIMIT} offset={offset} onOffset={setOffset} /> : undefined}
      >
        {loading ? (
          <Loading />
        ) : items.length === 0 ? (
          <Empty>No matching cache entries.</Empty>
        ) : (
          <Table cols={['Operation', 'Key', 'Source', 'Kind', 'Status', 'ms', 'Fetched', 'TTL left', 'State', '']}>
            {items.map((r) => (
              <tr key={r.id}>
                <td className="mono">{r.op || '—'}</td>
                <td className="mono">{r.queryKey}</td>
                <td>
                  <SourceChip source={r.source} />
                </td>
                <td>{r.queryKind}</td>
                <td className={r.httpStatus >= 400 ? 'text-bad' : undefined}>{r.httpStatus}</td>
                <td className="num">{r.latencyMs ?? '—'}</td>
                <td className="mono num">{fmtTime(r.fetchedAt, true)}</td>
                <td className="num">{until(r.expiresAt)}</td>
                <td>
                  <span className={cx('chip', STATE_CLASS[r.normalizeState])}>{r.normalizeState}</span>
                </td>
                <td>
                  <div className="flex gap-1.5">
                    <button className="btn btn-sm" onClick={() => detail(r.id)}>
                      Detail
                    </button>
                    <button className="btn btn-sm btn-danger" onClick={() => del(r.id)}>
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </DataBlock>
      <div className="mt-1 text-[11.5px] text-faint">
        The normalizer worker harvests each body into the parts store; a <Chip className="border-warn text-warn"><span>pending</span></Chip> row is
        still queued.
      </div>
    </>
  );
}
